import React, { useState, useEffect } from 'react';
import { getErrorDiagnostics, clearErrorDiagnostics, exportErrorDiagnostics } from './GlobalErrorHandler';

export const DebugOverlay: React.FC = () => {
  const [isVisible, setIsVisible] = useState(false);
  const [diagnostics, setDiagnostics] = useState<any[]>([]);

  useEffect(() => {
    const updateDiagnostics = () => {
      setDiagnostics(getErrorDiagnostics());
    };

    // Atualiza a cada segundo em desenvolvimento
    const interval = setInterval(updateDiagnostics, 1000);
    updateDiagnostics();

    return () => clearInterval(interval);
  }, []);

  // Só mostra em desenvolvimento
  if (process.env.NODE_ENV !== 'development') {
    return null;
  }

  const handleClear = () => {
    clearErrorDiagnostics();
    setDiagnostics([]);
  };

  const handleExport = () => {
    const data = exportErrorDiagnostics();
    const blob = new Blob([data], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `fe-diagnostics-${new Date().toISOString()}.json`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const errorCount = diagnostics.filter(d => d.type === 'onerror' || d.type === 'unhandledrejection').length;
  const boundaryCount = diagnostics.filter(d => d.type === 'boundary').length;
  const consoleCount = diagnostics.filter(d => d.type === 'console').length;

  return (
    <>
      {/* Botão flutuante */}
      <button
        onClick={() => setIsVisible(!isVisible)}
        className="fixed bottom-4 right-4 z-50 bg-red-500 hover:bg-red-600 text-white rounded-full w-12 h-12 flex items-center justify-center shadow-lg dark:shadow-2xl dark:shadow-slate-900/50"
        title="Debug Overlay"
      >
        {errorCount > 0 && (
          <span className="absolute -top-1 -right-1 bg-white text-red-500 rounded-full w-5 h-5 text-xs flex items-center justify-center font-bold">
            {errorCount}
          </span>
        )}
        🐛
      </button>

      {/* Overlay */}
      {isVisible && (
        <div className="fixed inset-0 z-40 bg-black bg-opacity-50 flex items-center justify-center p-4">
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-4xl w-full max-h-[80vh] overflow-hidden">
            <div className="flex items-center justify-between p-4 border-b border-gray-200 dark:border-slate-700 bg-gray-50 dark:bg-slate-700">
              <h2 className="text-lg font-semibold text-gray-900 dark:text-slate-100">Debug Overlay</h2>
              <div className="flex gap-2">
                <button
                  onClick={handleExport}
                  className="px-3 py-1 bg-blue-500 text-white rounded text-sm"
                >
                  Export
                </button>
                <button
                  onClick={handleClear}
                  className="px-3 py-1 bg-gray-50 dark:bg-slate-8000 text-white rounded text-sm"
                >
                  Clear
                </button>
                <button
                  onClick={() => setIsVisible(false)}
                  className="px-3 py-1 bg-red-500 text-white rounded text-sm"
                >
                  Close
                </button>
              </div>
            </div>

            <div className="p-4 overflow-auto max-h-[calc(80vh-80px)]">
              {/* Resumo */}
              <div className="grid grid-cols-3 gap-4 mb-4">
                <div className="bg-red-100 p-3 rounded">
                  <div className="text-red-800 font-bold">{errorCount}</div>
                  <div className="text-red-600 text-sm">Errors</div>
                </div>
                <div className="bg-yellow-100 p-3 rounded">
                  <div className="text-yellow-800 font-bold">{boundaryCount}</div>
                  <div className="text-yellow-600 text-sm">Boundaries</div>
                </div>
                <div className="bg-blue-100 p-3 rounded">
                  <div className="text-blue-800 font-bold">{consoleCount}</div>
                  <div className="text-blue-600 text-sm">Console</div>
                </div>
              </div>

              {/* Lista de diagnósticos */}
              {diagnostics.length === 0 ? (
                <div className="text-center text-gray-500 dark:text-slate-400 py-8">
                  Nenhum erro capturado ainda
                </div>
              ) : (
                <div className="space-y-2">
                  {diagnostics.map((diag, index) => (
                    <div
                      key={index}
                      className={`p-3 rounded border ${
                        diag.type === 'onerror' || diag.type === 'unhandledrejection'
                          ? 'bg-red-50 border-red-200'
                          : diag.type === 'boundary'
                          ? 'bg-yellow-50 border-yellow-200'
                          : 'bg-blue-50 border-blue-200'
                      }`}
                    >
                      <div className="flex items-center justify-between mb-2">
                        <span className={`text-xs font-bold px-2 py-1 rounded ${
                          diag.type === 'onerror' || diag.type === 'unhandledrejection'
                            ? 'bg-red-200 text-red-800'
                            : diag.type === 'boundary'
                            ? 'bg-yellow-200 text-yellow-800'
                            : 'bg-blue-200 text-blue-800'
                        }`}>
                          {diag.type.toUpperCase()}
                        </span>
                        <span className="text-xs text-gray-500 dark:text-slate-400">
                          {new Date(diag.timestamp).toLocaleTimeString()}
                        </span>
                      </div>
                      
                      <div className="text-sm font-mono break-all">
                        {diag.error || diag.reason || 'Sem mensagem'}
                      </div>
                      
                      {diag.url && (
                        <div className="text-xs text-gray-600 dark:text-slate-300 mt-1">
                          URL: {diag.url}
                        </div>
                      )}
                      
                      {diag.stack && (
                        <details className="mt-2">
                          <summary className="text-xs text-gray-600 dark:text-slate-300 cursor-pointer">
                            Stack trace
                          </summary>
                          <pre className="text-xs bg-gray-100 dark:bg-slate-700 p-2 rounded mt-1 overflow-auto max-h-32">
                            {diag.stack}
                          </pre>
                        </details>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </>
  );
};
